import { useMemo } from 'react';
import { Box, Flex, Icon, Center } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { KText } from '@/ui-library/components/text';
import { FRAUD_DETECTOR_BADGE_CONFIG, FraudDetectorStatus } from '../constants';

const rotateBorder = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

interface FraudDetectorBadgeProps {
  status: FraudDetectorStatus;
  showLabel?: boolean;
  variant?: 'default' | 'animated';
}

export const FraudDetectorBadge: React.FC<FraudDetectorBadgeProps> = ({
  status,
  showLabel = true,
  variant = 'default',
}) => {
  const config = FRAUD_DETECTOR_BADGE_CONFIG[status];

  const isInProgress = status === 'in_progress';

  const rippleAnimation = useMemo(() => {
    const hex = config.rippleColor;
    return keyframes`
      0% {
        box-shadow: 0 0 0 0 ${hex}40,
                    0 0 0 6px ${hex}33,
                    0 0 0 12px ${hex}26;
      }
      100% {
        box-shadow: 0 0 0 6px ${hex}33,
                    0 0 0 12px ${hex}26,
                    0 0 0 18px ${hex}00;
      }
    `;
  }, [config.rippleColor]);

  if (variant === 'animated') {
    return (
      <Box position="relative" w="24px" h="24px" flexShrink={0}>
        <Box
          position="absolute"
          w="24px"
          h="24px"
          borderRadius="50%"
          border="1px solid"
          borderColor={config.borderColor}
          bg={config.iconBg}
          animation={`${rippleAnimation} 0.5s linear infinite`}
        />
        <Center position="absolute" w="24px" h="24px" top="0" left="0">
          <Icon as={config.icon} boxSize="16px" color={config.iconColor} />
        </Center>
      </Box>
    );
  }

  if (showLabel) {
    return (
      <Flex
        alignItems="center"
        gap="6px"
        px="10px"
        py="4px"
        borderRadius="6px"
        border={isInProgress ? 'none' : '1.5px solid'}
        borderColor={!isInProgress ? config.borderColor : undefined}
        bg={config.iconBg}
        position="relative"
      >
        {isInProgress && (
          <Box
            as="svg"
            position="absolute"
            inset="0"
            w="100%"
            h="100%"
            pointerEvents="none"
            color={config.borderColor}
          >
            <rect
              x="0.75"
              y="0.75"
              rx="5.25"
              ry="5.25"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeDasharray="4 4"
              style={{
                width: 'calc(100% - 1.5px)',
                height: 'calc(100% - 1.5px)',
              }}
            >
              <animate
                attributeName="stroke-dashoffset"
                from="0"
                to="-16"
                dur="0.6s"
                repeatCount="indefinite"
              />
            </rect>
          </Box>
        )}
        <Icon
          as={config.icon}
          boxSize="16px"
          color={config.iconColor}
          flexShrink={0}
        />
        <KText
          fontSize="13px"
          fontWeight="500"
          lineHeight="150%"
          color={config.iconColor}
          whiteSpace="nowrap"
        >
          {config.label}
        </KText>
      </Flex>
    );
  }

  return (
    <Box position="relative" w="24px" h="24px" flexShrink={0}>
      <Box
        position="absolute"
        w="24px"
        h="24px"
        borderRadius="50%"
        border="1px"
        borderStyle={isInProgress ? 'dashed' : 'solid'}
        borderColor={config.borderColor}
        bg={config.iconBg}
        animation={
          isInProgress ? `${rotateBorder} 3s linear infinite` : undefined
        }
      />
      <Center position="absolute" w="24px" h="24px" top="0" left="0">
        <Icon as={config.icon} boxSize="16px" color={config.iconColor} />
      </Center>
    </Box>
  );
};
